import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminClinicLocationService } from '../services/admin/admin-clinic-location.service';
import { ClinicLocation } from '../common/model/dto/clinic-location';
import { AssignPatientToClinicLocationRequest } from '../common/model/dto/assign-user-to-clinic-location-request';
import { ClinicLocationService } from '../services/clinic-location.service';
import { CreateDemoAccountRequest } from '../common/model/dto/create-demo-account-request';
import { AssignProviderToClinicLocationRequest } from '../common/model/dto/assign-provider-to-clinic-location-request';
import { ClinicRole } from '../common/model/type/clinic-role';
import UserService from '../services/user.service';

export function useAdminClinicLocationMutations(clinicLocationId: string) {
  const qc = useQueryClient();

  const invalidateClinicLocationQuery = () =>
    qc.invalidateQueries({
      queryKey: ['admin-clinic-location', clinicLocationId],
    });

  const invalidateClinicLocationProviderQuery = () =>
    qc.invalidateQueries({
      queryKey: ['admin-clinic-location-providers'],
    });

  const invalidateClinicLocationPatientQuery = () =>
    qc.invalidateQueries({
      queryKey: ['admin-clinic-location-patients'],
    });

  const deleteClinicLocation = useMutation({
    mutationFn: () =>
      AdminClinicLocationService.deleteClinicLocation(clinicLocationId),
    onSuccess: () => {
      qc.removeQueries({
        queryKey: ['admin-clinic-location', clinicLocationId],
      });
      qc.invalidateQueries({ queryKey: ['admin-clinic-locations'] });
      qc.invalidateQueries({ queryKey: ['admin-clinics'] });
    },
  });

  const updateClinicLocation = useMutation({
    mutationFn: (updatedProperties: Partial<ClinicLocation>) =>
      AdminClinicLocationService.updateClinicLocation(
        clinicLocationId,
        updatedProperties
      ),
    onSuccess: () => {
      invalidateClinicLocationQuery();
      qc.invalidateQueries({ queryKey: ['admin-clinic-locations'] });
    },
  });

  const assignPatientToClinicLocation = useMutation({
    mutationFn: (request: AssignPatientToClinicLocationRequest) =>
      ClinicLocationService.assignPatientToClinicLocation(
        clinicLocationId,
        request
      ),
  });

  const createDemoAccount = useMutation({
    mutationFn: (request: CreateDemoAccountRequest) =>
      AdminClinicLocationService.createDemoAccount(request, clinicLocationId),
  });

  const assignProviderToClinicLocation = useMutation({
    mutationFn: (request: AssignProviderToClinicLocationRequest) =>
      AdminClinicLocationService.assignProviderToClinicLocation(
        clinicLocationId,
        request
      ),
    onSuccess: () => invalidateClinicLocationProviderQuery(),
  });

  const approveProviderInvitationForUser = useMutation({
    mutationFn: (userId: string) =>
      AdminClinicLocationService.approveProviderInvitationForUser(
        clinicLocationId,
        userId
      ),
    onSuccess: () => invalidateClinicLocationProviderQuery(),
  });

  const approvePatientInvitationForUser = useMutation({
    mutationFn: (userId: string) =>
      AdminClinicLocationService.approvePatientInvitationForUser(
        clinicLocationId,
        userId
      ),
    onSuccess: () => invalidateClinicLocationPatientQuery(),
  });

  const removeProviderFromClinicLocation = useMutation({
    mutationFn: ({
      userId,
      clinicRole,
    }: {
      userId: string;
      clinicRole: ClinicRole;
    }) =>
      AdminClinicLocationService.removeProviderFromClinicLocation(
        clinicLocationId,
        userId,
        clinicRole
      ),
    onSuccess: () => invalidateClinicLocationProviderQuery(),
  });

  const removePatientFromClinicLocation = useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      ClinicLocationService.removePatientFromClinicLocation(
        clinicLocationId,
        userId
      ),
    onSuccess: () => invalidateClinicLocationPatientQuery(),
  });

  const refreshSetPasswordToken = useMutation({
    mutationFn: (userId: string) => UserService.refreshSetPasswordToken(userId),
    onSuccess: () => invalidateClinicLocationProviderQuery(),
  });

  const refreshApproveInvitationToken = useMutation({
    mutationFn: (userId: string) =>
      AdminClinicLocationService.refreshApproveInvitationToken(userId),
    onSuccess: () => invalidateClinicLocationProviderQuery(),
  });

  return {
    deleteClinicLocation,
    updateClinicLocation,
    assignPatientToClinicLocation,
    createDemoAccount,
    assignProviderToClinicLocation,
    approveProviderInvitationForUser,
    approvePatientInvitationForUser,
    removeProviderFromClinicLocation,
    removePatientFromClinicLocation,

    refreshSetPasswordToken,
    refreshApproveInvitationToken,
  };
}
